import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.success) {
      localStorage.setItem("token", res.data.token);
      setToken(res.data.token);
      setUser({
        user_type: res.data.user_type,
        nome: res.data.nome,
        cognome: res.data.cognome,
        user_id: res.data.user_id,
      });
      return res.data;
    }
    throw new Error("Login fallito");
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== LOGIN PAGE ====================
const LoginPage = ({ onBack }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError("Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-2"
        >
          ← Torna al sito
        </button>
        
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Area Clienti</h1>
          <p className="text-gray-600 mt-2">Studio Dott. Alberto Grassetti</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Email / Username</label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="La tua email o username"
              required
              data-testid="login-email"
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="La tua password"
              required
              data-testid="login-password"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold rounded-lg transition-colors"
            data-testid="login-submit"
          >
            {loading ? "Accesso in corso..." : "Accedi"}
          </button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-6">
          Per richiedere le credenziali contatta lo studio
        </p>
      </div>
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================
const AdminDashboard = () => {
  const { user, logout, token } = useAuth();
  const [clienti, setClienti] = useState([]);
  const [messaggi, setMessaggi] = useState([]);
  const [activeTab, setActiveTab] = useState("clienti");
  const [showNewCliente, setShowNewCliente] = useState(false);
  const [showEditCliente, setShowEditCliente] = useState(null);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientiRes, messaggiRes] = await Promise.all([
        axios.get(`${API}/admin/clienti`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/messaggi`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setClienti(clientiRes.data);
      setMessaggi(messaggiRes.data);
    } catch (e) {
      console.error("Errore caricamento dati:", e);
    } finally {
      setLoading(false);
    }
  };

  const deleteCliente = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo cliente?")) return;
    try {
      await axios.delete(`${API}/admin/clienti/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClienti(clienti.filter((c) => c.id !== id));
    } catch (e) {
      alert("Errore eliminazione cliente");
    }
  };

  const handleUpdateCliente = (updatedCliente) => {
    setClienti(clienti.map(c => c.id === updatedCliente.id ? updatedCliente : c));
    setShowEditCliente(null);
  };

  const handleInviaBenvenutoTutti = async () => {
    const conferma = window.confirm(
      "Vuoi inviare l'email di benvenuto a tutti i clienti che non l'hanno ancora ricevuta?\n\n" +
      "Ogni cliente riceverà un link per impostare la propria password."
    );
    if (!conferma) return;
    
    setSendingEmails(true);
    try {
      const response = await axios.post(
        `${API}/admin/clienti/invia-benvenuto-tutti`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { totale, inviati, errori } = response.data;
      
      let messaggio = `✅ Email inviate: ${inviati}/${totale}`;
      if (errori && errori.length > 0) {
        messaggio += `\n\n⚠️ Errori:\n${errori.slice(0, 5).join('\n')}`;
        if (errori.length > 5) {
          messaggio += `\n...e altri ${errori.length - 5} errori`;
        }
      }
      
      alert(messaggio);
      loadData(); // Ricarica per aggiornare lo stato
    } catch (e) {
      alert("Errore durante l'invio delle email: " + (e.response?.data?.detail || e.message));
    } finally {
      setSendingEmails(false);
    }
  };

  const handleInviaBenvenutoSingolo = async (cliente) => {
    const conferma = window.confirm(
      `Vuoi inviare l'email di benvenuto a ${cliente.nome} ${cliente.cognome}?\n\n` +
      `Email: ${cliente.email}`
    );
    if (!conferma) return;
    
    try {
      await axios.post(
        `${API}/admin/clienti/${cliente.id}/invia-benvenuto`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`✅ Email inviata a ${cliente.email}`);
      loadData();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo messaggio?")) return;
    try {
      await axios.delete(`${API}/admin/messaggi/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessaggi(messaggi.filter((m) => m.id !== id));
    } catch (e) {
      alert("Errore eliminazione messaggio");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-900 text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Pannello Amministrazione</h1>
            <p className="text-blue-200 text-sm">Studio Grassetti</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-blue-200">Ciao, {user?.nome}</span>
            <button
              onClick={logout}
              className="px-4 py-2 bg-blue-800 hover:bg-blue-700 rounded-lg text-sm"
            >
              Esci
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex gap-4 border-b border-gray-300">
          <button
            onClick={() => setActiveTab("clienti")}
            className={`px-4 py-2 font-medium ${
              activeTab === "clienti"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            👥 Clienti ({clienti.length})
          </button>
          <button
            onClick={() => setActiveTab("messaggi")}
            className={`px-4 py-2 font-medium ${
              activeTab === "messaggi"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            📬 Messaggi ({messaggi.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : activeTab === "clienti" ? (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Gestione Clienti</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowImportCSV(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Importa CSV
                </button>
                <button
                  onClick={handleInviaBenvenutoTutti}
                  disabled={sendingEmails}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium flex items-center gap-2"
                  title="Invia email di benvenuto a tutti i clienti che non l'hanno ancora ricevuta"
                >
                  {sendingEmails ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Invio...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email a Tutti
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowNewCliente(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                >
                  + Nuovo Cliente
                </button>
              </div>
            </div>

            {clienti.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                Nessun cliente registrato
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nome</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Telefono</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Stato</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {clienti.map((cliente) => (
                      <tr key={cliente.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">
                            {cliente.nome} {cliente.cognome}
                          </div>
                          {cliente.codice_fiscale && (
                            <div className="text-xs text-gray-500">{cliente.codice_fiscale}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{cliente.email}</td>
                        <td className="px-4 py-3 text-gray-600">{cliente.telefono || "-"}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium inline-block w-fit ${
                                cliente.attivo
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {cliente.attivo ? "Attivo" : "Disattivato"}
                            </span>
                            {cliente.email_benvenuto_inviata ? (
                              <span className="text-xs text-green-600">✓ Email inviata</span>
                            ) : (
                              <span className="text-xs text-orange-500">⚠ Email non inviata</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => setShowEditCliente(cliente)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Modifica
                            </button>
                            {!cliente.email_benvenuto_inviata && (
                              <button
                                onClick={() => handleInviaBenvenutoSingolo(cliente)}
                                className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                              >
                                Invia Email
                              </button>
                            )}
                            <button
                              onClick={() => deleteCliente(cliente.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Elimina
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Modal Nuovo Cliente */}
            {showNewCliente && (
              <NewClienteModal
                onClose={() => setShowNewCliente(false)}
                onSave={(newCliente) => {
                  setClienti([...clienti, newCliente]);
                  setShowNewCliente(false);
                }}
                token={token}
              />
            )}

            {/* Modal Modifica Cliente */}
            {showEditCliente && (
              <EditClienteModal
                cliente={showEditCliente}
                onClose={() => setShowEditCliente(null)}
                onSave={handleUpdateCliente}
                token={token}
              />
            )}

            {/* Modal Import CSV */}
            {showImportCSV && (
              <ImportCSVModal
                onClose={() => setShowImportCSV(false)}
                onImport={() => {
                  loadData();
                  setShowImportCSV(false);
                }}
                token={token}
              />
            )}
          </div>
        ) : (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-6">Messaggi Ricevuti</h2>
            {messaggi.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                Nessun messaggio
              </div>
            ) : (
              <div className="space-y-4">
                {messaggi.map((msg) => (
                  <div key={msg.id} className="bg-white rounded-xl p-6 shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900">{msg.nome}</h3>
                        <p className="text-sm text-gray-600">{msg.email}</p>
                        {msg.telefono && <p className="text-sm text-gray-600">{msg.telefono}</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">
                          {new Date(msg.timestamp).toLocaleString("it-IT")}
                        </span>
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          className="text-red-500 hover:text-red-700 text-sm"
                          title="Elimina messaggio"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{msg.messaggio}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== NEW CLIENTE MODAL ====================
const NewClienteModal = ({ onClose, onSave, token }) => {
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    password: "",
    telefono: "",
    cellulare: "",
    azienda: "",
    codice_fiscale: "",
    partita_iva: "",
    indirizzo: "",
    invia_email: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/clienti`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSave(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore creazione cliente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Nuovo Cliente</h2>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
              <input
                type="text"
                required
                value={form.cognome}
                onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Password per il cliente"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Azienda / Ragione Sociale</label>
            <input
              type="text"
              value={form.azienda}
              onChange={(e) => setForm({ ...form, azienda: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Nome azienda o ditta"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono Fisso</label>
              <input
                type="tel"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="es. 0427 51251"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cellulare</label>
              <input
                type="tel"
                value={form.cellulare}
                onChange={(e) => setForm({ ...form, cellulare: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="es. 333 1234567"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
              <input
                type="text"
                value={form.codice_fiscale}
                onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
              <input
                type="text"
                value={form.partita_iva}
                onChange={(e) => setForm({ ...form, partita_iva: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
            <input
              type="text"
              value={form.indirizzo}
              onChange={(e) => setForm({ ...form, indirizzo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.invia_email}
                onChange={(e) => setForm({ ...form, invia_email: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-800">Invia email di benvenuto</span>
                <p className="text-xs text-gray-500">Il cliente riceverà le credenziali via email</p>
              </div>
            </label>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium"
            >
              {loading ? "Creazione..." : "Crea Cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== EDIT CLIENTE MODAL ====================
const EditClienteModal = ({ cliente, onClose, onSave, token }) => {
  const [form, setForm] = useState({
    nome: cliente.nome || "",
    cognome: cliente.cognome || "",
    email: cliente.email || "",
    telefono: cliente.telefono || "",
    cellulare: cliente.cellulare || "",
    azienda: cliente.azienda || "",
    codice_fiscale: cliente.codice_fiscale || "",
    partita_iva: cliente.partita_iva || "",
    indirizzo: cliente.indirizzo || "",
    attivo: cliente.attivo !== false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.put(`${API}/admin/clienti/${cliente.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSave(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore aggiornamento cliente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Modifica Cliente</h2>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
              <input
                type="text"
                required
                value={form.cognome}
                onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Azienda / Ragione Sociale</label>
            <input
              type="text"
              value={form.azienda}
              onChange={(e) => setForm({ ...form, azienda: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono Fisso</label>
              <input
                type="text"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cellulare</label>
              <input
                type="text"
                value={form.cellulare}
                onChange={(e) => setForm({ ...form, cellulare: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
              <input
                type="text"
                value={form.codice_fiscale}
                onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
              <input
                type="text"
                value={form.partita_iva}
                onChange={(e) => setForm({ ...form, partita_iva: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
            <input
              type="text"
              value={form.indirizzo}
              onChange={(e) => setForm({ ...form, indirizzo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.attivo}
                onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-800">Cliente attivo</span>
                <p className="text-xs text-gray-500">Disattiva per impedire l'accesso al cliente</p>
              </div>
            </label>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium"
            >
              {loading ? "Salvataggio..." : "Salva Modifiche"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== IMPORT CSV MODAL ====================
const ImportCSVModal = ({ onClose, onImport, token }) => {
  const [file, setFile] = useState(null);
  const [inviaEmail, setInviaEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [risultato, setRisultato] = useState(null);
  const [error, setError] = useState("");

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/clienti/template/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_clienti.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Errore download template");
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setLoading(true);
    setError("");
    setRisultato(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/admin/clienti/import/csv?invia_email=${inviaEmail}`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      
      setRisultato(response.data);
      if (response.data.importati > 0) {
        onImport();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Errore durante l'import");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Importa Clienti da CSV</h2>
        <p className="text-sm text-gray-500 mb-6">Carica un file CSV con i dati dei clienti da importare</p>

        {!risultato ? (
          <>
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-blue-800 mb-2">📋 Template CSV</h3>
              <p className="text-sm text-blue-700 mb-3">
                Scarica il template per vedere il formato corretto del file CSV.
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
              >
                ⬇️ Scarica Template
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Seleziona file CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {file && (
                <p className="mt-2 text-sm text-green-600">✓ File selezionato: {file.name}</p>
              )}
            </div>

            <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inviaEmail}
                  onChange={(e) => setInviaEmail(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="font-medium text-gray-800">Invia email di benvenuto</span>
                  <p className="text-xs text-gray-500">Ogni cliente importato riceverà le credenziali via email</p>
                </div>
              </label>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleImport}
                disabled={!file || loading}
                className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium"
              >
                {loading ? "Importazione..." : "Importa Clienti"}
              </button>
            </div>
          </>
        ) : (
          <div>
            <div className={`p-4 rounded-lg mb-4 ${risultato.importati > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <h3 className="font-bold text-lg mb-2">
                {risultato.importati > 0 ? '✅ Import completato!' : '⚠️ Import completato con errori'}
              </h3>
              <div className="space-y-1 text-sm">
                <p><strong>Totale righe:</strong> {risultato.totale}</p>
                <p className="text-green-700"><strong>Importati con successo:</strong> {risultato.importati}</p>
                <p className="text-red-700"><strong>Errori:</strong> {risultato.errori?.length || 0}</p>
              </div>
            </div>

            {risultato.errori && risultato.errori.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200 max-h-40 overflow-y-auto">
                <h4 className="font-medium text-red-800 mb-2">Dettaglio errori:</h4>
                <ul className="text-xs text-red-700 space-y-1">
                  {risultato.errori.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CLIENTE DASHBOARD ====================
const ClienteDashboard = () => {
  const { user, logout, token } = useAuth();
  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messaggio, setMessaggio] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    loadDocumenti();
  }, []);

  const loadDocumenti = async () => {
    try {
      const res = await axios.get(`${API}/cliente/documenti`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocumenti(res.data.documenti || []);
      setMessaggio(res.data.messaggio || "");
    } catch (e) {
      console.error("Errore caricamento documenti:", e);
      setMessaggio("Errore nel caricamento dei documenti");
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const response = await axios.get(`${API}/cliente/documenti/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Crea un link per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nome);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Errore download:", e);
      alert("Errore nel download del documento");
    } finally {
      setDownloadingId(null);
    }
  };

  const getFileIcon = (tipo) => {
    if (tipo?.includes('pdf')) return '📕';
    if (tipo?.includes('spreadsheet') || tipo?.includes('excel')) return '📊';
    if (tipo?.includes('document') || tipo?.includes('word')) return '📘';
    if (tipo?.includes('image')) return '🖼️';
    return '📄';
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString("it-IT", {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Raggruppa documenti per cartella
  const documentiPerCartella = documenti.reduce((acc, doc) => {
    const cartella = doc.cartella || 'Generale';
    if (!acc[cartella]) acc[cartella] = [];
    acc[cartella].push(doc);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white py-4 px-6 shadow-lg">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Area Clienti</h1>
            <p className="text-cyan-300 text-sm">Studio Dott. Alberto Grassetti</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-blue-200">
              Benvenuto, {user?.nome} {user?.cognome}
            </span>
            <button
              onClick={logout}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm transition-colors"
            >
              Esci
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <span className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </span>
              I Tuoi Documenti
            </h2>
            <button 
              onClick={loadDocumenti}
              className="px-4 py-2 text-cyan-600 hover:bg-cyan-50 rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Aggiorna
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Caricamento documenti...</p>
            </div>
          ) : documenti.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <p className="text-slate-600 mb-2 font-medium">{messaggio || "Nessun documento disponibile"}</p>
              <p className="text-sm text-slate-500">
                I documenti caricati dallo studio appariranno qui
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(documentiPerCartella).map(([cartella, docs]) => (
                <div key={cartella}>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    {cartella}
                  </h3>
                  <div className="grid gap-3">
                    {docs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border border-slate-100"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="text-3xl flex-shrink-0">{getFileIcon(doc.tipo)}</div>
                          <div className="min-w-0">
                            <h4 className="font-medium text-slate-900 truncate">{doc.nome}</h4>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span>{formatDate(doc.data_modifica)}</span>
                              {doc.dimensione > 0 && (
                                <>
                                  <span>•</span>
                                  <span>{formatFileSize(doc.dimensione)}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => downloadDocument(doc)}
                          disabled={downloadingId === doc.id}
                          className="flex-shrink-0 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                        >
                          {downloadingId === doc.id ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Download...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Scarica
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              <div className="text-center pt-4 text-sm text-slate-500">
                {documenti.length} documento{documenti.length !== 1 ? 'i' : ''} disponibile{documenti.length !== 1 ? 'i' : ''}
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
          <h3 className="font-bold text-cyan-900 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Informazioni
          </h3>
          <p className="text-cyan-800 text-sm">
            In quest'area troverai tutti i documenti che lo studio ha preparato per te: 
            dichiarazioni, bilanci, cedolini e altri documenti importanti. 
            Per qualsiasi domanda contatta lo studio al +39 0427 51251.
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== NAVBAR ====================
const Navbar = ({ onLoginClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-lg py-3" : "py-4"
      }`}
      style={{ backgroundColor: isScrolled ? '#ffffff' : '#031947' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <span className={`text-xl font-semibold ${isScrolled ? "text-gray-800" : "text-white"}`}
              style={{ color: isScrolled ? '#031947' : '#ffffff' }}>
              Studio Grassetti
            </span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {["home", "servizi", "chi-siamo"].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`font-medium transition-all duration-200 ${
                  isScrolled 
                    ? "text-gray-600 hover:font-semibold" 
                    : "text-white/80 hover:text-white hover:font-semibold hover:scale-105"
                }`}
                style={{ 
                  color: isScrolled ? '#031947' : 'rgba(255,255,255,0.9)'
                }}
                onMouseEnter={(e) => e.target.style.color = isScrolled ? '#00A6FF' : '#ffffff'}
                onMouseLeave={(e) => e.target.style.color = isScrolled ? '#031947' : 'rgba(255,255,255,0.9)'}
              >
                {item === "chi-siamo"
                  ? "Chi Siamo"
                  : item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
            
            {/* Dropdown Strumenti */}
            <div className="relative group">
              <button
                className={`font-medium transition-all duration-200 flex items-center gap-1 ${
                  isScrolled 
                    ? "text-gray-600 hover:font-semibold" 
                    : "text-white/80 hover:text-white hover:font-semibold"
                }`}
                style={{ 
                  color: isScrolled ? '#031947' : 'rgba(255,255,255,0.9)'
                }}
              >
                Strumenti
                <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button onClick={() => scrollToSection('scadenze')} className="w-full px-4 py-2.5 text-left text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 flex items-center gap-3">
                    <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </span>
                    Scadenze Fiscali
                  </button>
                  <button onClick={() => scrollToSection('calcolatore')} className="w-full px-4 py-2.5 text-left text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 flex items-center gap-3">
                    <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </span>
                    Calcolatore Tasse
                  </button>
                  <button onClick={() => scrollToSection('faq')} className="w-full px-4 py-2.5 text-left text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 flex items-center gap-3">
                    <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                    FAQ
                  </button>
                  <button onClick={() => scrollToSection('risorse')} className="w-full px-4 py-2.5 text-left text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 flex items-center gap-3">
                    <span className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </span>
                    Risorse Utili
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('contatti')}
              className={`font-medium transition-all duration-200 ${
                isScrolled 
                  ? "text-gray-600 hover:font-semibold" 
                  : "text-white/80 hover:text-white hover:font-semibold hover:scale-105"
              }`}
              style={{ 
                color: isScrolled ? '#031947' : 'rgba(255,255,255,0.9)'
              }}
              onMouseEnter={(e) => e.target.style.color = isScrolled ? '#00A6FF' : '#ffffff'}
              onMouseLeave={(e) => e.target.style.color = isScrolled ? '#031947' : 'rgba(255,255,255,0.9)'}
            >
              Contatti
            </button>
            
            <button
              onClick={onLoginClick}
              className="px-5 py-2.5 rounded-lg font-medium transition-all duration-300 hover:shadow-lg hover:scale-105"
              style={{ 
                backgroundColor: isScrolled ? '#00A6FF' : '#ffffff',
                color: isScrolled ? '#ffffff' : '#031947'
              }}
            >
              Area Clienti
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <svg
              className={`w-6 h-6 ${isScrolled ? "text-gray-700" : "text-white"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
            {["home", "servizi", "chi-siamo"].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50"
              >
                {item === "chi-siamo"
                  ? "Chi Siamo"
                  : item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
            <div className="border-t border-gray-100 my-2"></div>
            <p className="px-4 py-1 text-xs text-gray-400 uppercase tracking-wider">Strumenti</p>
            <button onClick={() => scrollToSection('scadenze')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50">
              📅 Scadenze Fiscali
            </button>
            <button onClick={() => scrollToSection('calcolatore')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50">
              🧮 Calcolatore Tasse
            </button>
            <button onClick={() => scrollToSection('faq')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50">
              ❓ FAQ
            </button>
            <button onClick={() => scrollToSection('risorse')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50">
              🔗 Risorse Utili
            </button>
            <div className="border-t border-gray-100 my-2"></div>
            <button onClick={() => scrollToSection('contatti')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50">
              Contatti
            </button>
            <button
              onClick={onLoginClick}
              className="block w-full text-left px-4 py-2 text-blue-600 font-medium hover:bg-blue-50"
            >
              🔐 Area Clienti
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

// ==================== HERO SECTION ====================
const HeroSection = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="min-h-screen flex items-center justify-center pt-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #031947 0%, #052a5e 50%, #031947 100%)' }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl" style={{ backgroundColor: '#00A6FF' }}></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#00A6FF' }}></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badge */}
        <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
          <span className="text-white/90 text-sm font-medium">
            Il Vostro Partner di Fiducia dal 1993
          </span>
        </div>

        {/* Main Title */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
          Studio Dott. Alberto Grassetti
        </h1>
        
        {/* Subtitle */}
        <p className="text-xl md:text-2xl font-light mb-6" style={{ color: '#00A6FF' }}>
          Commercialista · Revisore Contabile
        </p>

        {/* Decorative line */}
        <div className="w-20 h-1 mx-auto mb-8 rounded-full" style={{ backgroundColor: '#00A6FF' }}></div>

        {/* Description */}
        <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
          Consulenza fiscale, societaria e del lavoro per imprese e professionisti.
          <br className="hidden md:block" />
          Il punto di riferimento a Spilimbergo e in tutto il Friuli-Venezia Giulia.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <button
            onClick={() => scrollToSection("contatti")}
            className="px-8 py-4 font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105"
            style={{ backgroundColor: '#00A6FF', color: '#ffffff' }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#33b8ff'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = '#00A6FF'; }}
            data-testid="cta-contact-btn"
          >
            Richiedi una Consulenza
          </button>
          <button
            onClick={() => scrollToSection("servizi")}
            className="px-8 py-4 bg-transparent font-bold rounded-lg border-2 border-white transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105"
            style={{ color: '#ffffff' }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#00A6FF'; e.target.style.borderColor = '#00A6FF'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#ffffff'; }}
            data-testid="cta-services-btn"
          >
            Scopri i Servizi
          </button>
        </div>

        {/* Trust indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto pt-8 border-t border-white/20">
          {[
            { value: "30+", label: "Anni di Esperienza" },
            { value: "300+", label: "Clienti Attivi" },
            { value: "100%", label: "Riservatezza" },
            { value: "24/7", label: "Assistenza AI" },
          ].map((item, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {item.value}
              </div>
              <div className="text-sm" style={{ color: '#00A6FF' }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
};

// ==================== SERVICES SECTION ====================
const ServicesSection = () => {
  const services = [
    {
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Consulenza Fiscale e Societaria",
      description:
        "Ottimizzazione del carico tributario e gestione completa degli adempimenti fiscali.",
      items: [
        "Pianificazione fiscale strategica",
        "Gestione contabilità ordinaria e semplificata",
        "Costituzione società e start-up",
        "Verifiche e contenzioso tributario",
      ],
    },
    {
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Consulenza del Lavoro",
      description:
        "Gestione completa delle risorse umane e degli adempimenti giuslavoristici.",
      items: [
        "Elaborazione cedolini paga",
        "Assunzioni e cessazioni",
        "Gestione previdenza e CCNL",
        "Compliance normativa del lavoro",
      ],
    },
    {
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: "Servizi Amministrativi",
      description:
        "Supporto operativo per una gestione aziendale efficiente e digitale.",
      items: [
        "Data entry e archiviazione digitale",
        "Contrattualistica aziendale",
        "Fatturazione elettronica",
        "Reportistica e controllo di gestione",
      ],
    },
  ];

  return (
    <section
      id="servizi"
      data-testid="services-section"
      className="py-24 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 font-semibold text-sm rounded-full mb-4"
            style={{ backgroundColor: 'rgba(0, 166, 255, 0.1)', color: '#00A6FF' }}>
            I Nostri Servizi
          </span>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#031947' }}>
            Eccellenza su Misura per la Vostra Azienda
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Un pacchetto completo di servizi professionali pensato per coprire
            ogni esigenza della vostra attività.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100 group hover:-translate-y-1"
              data-testid={`service-card-${index}`}
            >
              <div className="w-16 h-16 rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform shadow-lg"
                style={{ background: 'linear-gradient(135deg, #00A6FF 0%, #031947 100%)' }}>
                {service.icon}
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#031947' }}>
                {service.title}
              </h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">{service.description}</p>
              <ul className="space-y-3">
                {service.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00A6FF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== ABOUT SECTION ====================
const AboutSection = () => {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Radicamento Territoriale",
      description:
        "Conosciamo profondamente il tessuto economico di Spilimbergo e del Friuli-Venezia Giulia, offrendo soluzioni specifiche per le realtà locali.",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Approccio Proattivo",
      description:
        "Non ci limitiamo a registrare i fatti passati. Vi affianchiamo con consulenza proattiva per anticipare problemi e cogliere opportunità.",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      ),
      title: "Team Qualificato",
      description:
        "Professionisti costantemente aggiornati sulle ultime novità legislative e fiscali, garantendovi la massima affidabilità.",
    },
  ];

  const stats = [
    { value: "30+", label: "Anni di Esperienza" },
    { value: "300+", label: "Clienti Soddisfatti" },
    { value: "24/7", label: "Assistenza A.I." },
    { value: "100%", label: "Dedicazione" },
  ];

  return (
    <section
      id="chi-siamo"
      data-testid="about-section"
      className="py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 font-semibold text-sm rounded-full mb-4"
            style={{ backgroundColor: 'rgba(0, 166, 255, 0.1)', color: '#00A6FF' }}>
            Chi Siamo
          </span>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#031947' }}>
            Il Vostro Successo è la Nostra Missione
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <div key={index} className="text-center p-8 rounded-2xl transition-colors group hover:bg-gray-50">
              <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 transition-all group-hover:scale-110"
                style={{ backgroundColor: 'rgba(0, 166, 255, 0.1)', color: '#00A6FF' }}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#031947' }}>
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="rounded-3xl p-10 md:p-14 shadow-2xl"
          style={{ background: 'linear-gradient(135deg, #031947 0%, #00A6FF 100%)' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="text-3xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-white/70">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== RISORSE UTILI SECTION ====================
const RisorseUtiliSection = () => {
  const risorse = [
    {
      nome: "Il Sole 24 Ore",
      descrizione: "Notizie fiscali, finanziarie e normative aggiornate quotidianamente",
      url: "https://ilsole24ore.com",
      colore: "from-orange-500 to-amber-500",
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
    },
    {
      nome: "Agenzia delle Entrate",
      descrizione: "Portale ufficiale per adempimenti fiscali e servizi telematici",
      url: "https://www.agenziaentrate.gov.it",
      colore: "from-blue-600 to-blue-700",
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
        </svg>
      ),
    },
    {
      nome: "INPS",
      descrizione: "Previdenza sociale, contributi e servizi per lavoratori e aziende",
      url: "https://www.inps.it",
      colore: "from-teal-500 to-teal-600",
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      nome: "Camera di Commercio PN-UD",
      descrizione: "Registro imprese, visure camerali e servizi per le aziende",
      url: "https://www.pnud.camcom.it",
      colore: "from-indigo-500 to-indigo-600",
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];

  return (
    <section id="risorse" className="py-20 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden" data-testid="risorse-section">
      {/* Decorazioni sfondo */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-cyan-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-cyan-100 text-cyan-700 text-sm font-medium rounded-full mb-4">
            Rimani Informato
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            Risorse Utili
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Link diretti alle principali fonti istituzionali e informative per rimanere sempre aggiornati su normative, scadenze e novità fiscali
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {risorse.map((risorsa, index) => (
            <a
              key={index}
              href={risorsa.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl border border-slate-100 hover:border-cyan-200 transition-all duration-300 hover:-translate-y-1"
              data-testid={`risorsa-${index}`}
            >
              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${risorsa.colore} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                {risorsa.icon}
              </div>
              
              {/* Content */}
              <h3 className="text-lg font-semibold text-slate-800 mb-2 group-hover:text-cyan-600 transition-colors">
                {risorsa.nome}
              </h3>
              <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                {risorsa.descrizione}
              </p>
              
              {/* Link indicator */}
              <div className="flex items-center text-cyan-600 text-sm font-medium">
                <span>Visita il sito</span>
                <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
            </a>
          ))}
        </div>

        {/* Info box */}
        <div className="mt-12 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 md:p-8 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-white/80 text-sm md:text-base">
              <span className="text-white font-medium">Hai bisogno di assistenza?</span> Il nostro studio è a disposizione per aiutarti a navigare tra normative e adempimenti fiscali.
            </p>
            <a href="#contatti" className="px-6 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-white font-medium rounded-full transition-colors whitespace-nowrap">
              Contattaci
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== SCADENZE FISCALI SECTION ====================
const ScadenzeFiscaliSection = () => {
  const [meseCorrente, setMeseCorrente] = useState(new Date().getMonth());
  const [annoCorrente, setAnnoCorrente] = useState(new Date().getFullYear());

  const mesi = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ];

  // Scadenze fiscali 2025/2026 - dati realistici
  const scadenze = [
    // Gennaio
    { giorno: 16, mese: 0, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 16, mese: 0, titolo: "Ritenute d'acconto", tipo: "ritenute", descrizione: "Versamento ritenute dipendenti e autonomi" },
    { giorno: 25, mese: 0, titolo: "INTRASTAT mensile", tipo: "dichiarazione", descrizione: "Operatori con obbligo mensile" },
    { giorno: 31, mese: 0, titolo: "Imposta di bollo e-fatture", tipo: "iva", descrizione: "IV trimestre anno precedente" },
    // Febbraio
    { giorno: 16, mese: 1, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 16, mese: 1, titolo: "Contributi INPS", tipo: "contributi", descrizione: "Versamento contributi dipendenti" },
    { giorno: 28, mese: 1, titolo: "Comunicazione LIPE", tipo: "dichiarazione", descrizione: "Liquidazioni periodiche IVA IV trim." },
    // Marzo
    { giorno: 16, mese: 2, titolo: "Versamento IVA annuale", tipo: "iva", descrizione: "Saldo IVA anno precedente" },
    { giorno: 16, mese: 2, titolo: "INPS Gestione Separata", tipo: "contributi", descrizione: "Saldo e I acconto contributi" },
    { giorno: 31, mese: 2, titolo: "Bilancio d'esercizio", tipo: "dichiarazione", descrizione: "Approvazione bilancio (120 giorni)" },
    // Aprile
    { giorno: 16, mese: 3, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 30, mese: 3, titolo: "Imposta di bollo e-fatture", tipo: "iva", descrizione: "I trimestre" },
    // Maggio
    { giorno: 16, mese: 4, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 16, mese: 4, titolo: "IMU acconto", tipo: "imposte", descrizione: "Prima rata IMU" },
    { giorno: 31, mese: 4, titolo: "LIPE I trimestre", tipo: "dichiarazione", descrizione: "Comunicazione liquidazioni IVA" },
    // Giugno
    { giorno: 16, mese: 5, titolo: "IMU acconto", tipo: "imposte", descrizione: "Versamento acconto IMU" },
    { giorno: 30, mese: 5, titolo: "IRPEF/IRES saldo e acconto", tipo: "imposte", descrizione: "Imposte sui redditi" },
    { giorno: 30, mese: 5, titolo: "IRAP saldo e acconto", tipo: "imposte", descrizione: "Imposta regionale attività produttive" },
    { giorno: 30, mese: 5, titolo: "Diritto camerale", tipo: "imposte", descrizione: "Versamento annuale CCIAA" },
    // Luglio
    { giorno: 16, mese: 6, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 31, mese: 6, titolo: "Imposte con maggiorazione", tipo: "imposte", descrizione: "IRPEF/IRES/IRAP +0,40%" },
    { giorno: 31, mese: 6, titolo: "Imposta di bollo e-fatture", tipo: "iva", descrizione: "II trimestre" },
    // Agosto
    { giorno: 20, mese: 7, titolo: "Versamenti sospesi", tipo: "imposte", descrizione: "Ripresa versamenti agosto" },
    // Settembre
    { giorno: 16, mese: 8, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 30, mese: 8, titolo: "LIPE II trimestre", tipo: "dichiarazione", descrizione: "Comunicazione liquidazioni IVA" },
    // Ottobre
    { giorno: 16, mese: 9, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 31, mese: 9, titolo: "Mod. 770", tipo: "dichiarazione", descrizione: "Dichiarazione sostituti d'imposta" },
    { giorno: 31, mese: 9, titolo: "Imposta di bollo e-fatture", tipo: "iva", descrizione: "III trimestre" },
    // Novembre
    { giorno: 16, mese: 10, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 30, mese: 10, titolo: "II acconto imposte", tipo: "imposte", descrizione: "IRPEF/IRES/IRAP secondo acconto" },
    { giorno: 30, mese: 10, titolo: "LIPE III trimestre", tipo: "dichiarazione", descrizione: "Comunicazione liquidazioni IVA" },
    { giorno: 30, mese: 10, titolo: "Dichiarazione Redditi", tipo: "dichiarazione", descrizione: "Mod. Redditi PF/SP/SC" },
    // Dicembre
    { giorno: 16, mese: 11, titolo: "Versamento IVA mensile", tipo: "iva", descrizione: "Versamento IVA mese precedente" },
    { giorno: 16, mese: 11, titolo: "IMU saldo", tipo: "imposte", descrizione: "Versamento saldo IMU" },
    { giorno: 27, mese: 11, titolo: "Acconto IVA", tipo: "iva", descrizione: "Versamento acconto IVA annuale" },
  ];

  const scadenzeMese = scadenze.filter(s => s.mese === meseCorrente);

  const getTipoColor = (tipo) => {
    switch(tipo) {
      case 'iva': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'imposte': return 'bg-red-100 text-red-700 border-red-200';
      case 'contributi': return 'bg-green-100 text-green-700 border-green-200';
      case 'ritenute': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'dichiarazione': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTipoLabel = (tipo) => {
    switch(tipo) {
      case 'iva': return 'IVA';
      case 'imposte': return 'Imposte';
      case 'contributi': return 'Contributi';
      case 'ritenute': return 'Ritenute';
      case 'dichiarazione': return 'Dichiarazione';
      default: return tipo;
    }
  };

  const cambioMese = (direzione) => {
    if (direzione === 'prev') {
      if (meseCorrente === 0) {
        setMeseCorrente(11);
        setAnnoCorrente(annoCorrente - 1);
      } else {
        setMeseCorrente(meseCorrente - 1);
      }
    } else {
      if (meseCorrente === 11) {
        setMeseCorrente(0);
        setAnnoCorrente(annoCorrente + 1);
      } else {
        setMeseCorrente(meseCorrente + 1);
      }
    }
  };

  const oggi = new Date();
  const isScadenzaVicina = (giorno, mese) => {
    const dataScadenza = new Date(annoCorrente, mese, giorno);
    const diffGiorni = Math.ceil((dataScadenza - oggi) / (1000 * 60 * 60 * 24));
    return diffGiorni >= 0 && diffGiorni <= 7;
  };

  const isScadenzaPassata = (giorno, mese) => {
    const dataScadenza = new Date(annoCorrente, mese, giorno);
    return dataScadenza < oggi;
  };

  return (
    <section id="scadenze" className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" data-testid="scadenze-section">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-30 translate-x-1/2 -translate-y-1/2"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-full mb-4">
            Non perdere le scadenze
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            Scadenze Fiscali
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Tieni sotto controllo tutte le scadenze fiscali importanti. Il nostro studio è a disposizione per assisterti negli adempimenti.
          </p>
        </div>

        {/* Calendario Navigator */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden max-w-4xl mx-auto">
          {/* Header Mese */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4 flex items-center justify-between">
            <button 
              onClick={() => cambioMese('prev')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-xl font-semibold text-white">
              {mesi[meseCorrente]} {annoCorrente}
            </h3>
            <button 
              onClick={() => cambioMese('next')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Lista Scadenze */}
          <div className="p-6">
            {scadenzeMese.length > 0 ? (
              <div className="space-y-3">
                {scadenzeMese.sort((a, b) => a.giorno - b.giorno).map((scadenza, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md ${
                      isScadenzaPassata(scadenza.giorno, scadenza.mese) 
                        ? 'bg-gray-50 border-gray-200 opacity-60' 
                        : isScadenzaVicina(scadenza.giorno, scadenza.mese)
                          ? 'bg-red-50 border-red-200 shadow-sm'
                          : 'bg-white border-slate-200'
                    }`}
                  >
                    {/* Data */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center ${
                      isScadenzaPassata(scadenza.giorno, scadenza.mese)
                        ? 'bg-gray-200'
                        : isScadenzaVicina(scadenza.giorno, scadenza.mese)
                          ? 'bg-red-500'
                          : 'bg-cyan-500'
                    }`}>
                      <span className="text-white text-lg font-bold">{scadenza.giorno}</span>
                      <span className="text-white/80 text-xs uppercase">{mesi[scadenza.mese].slice(0,3)}</span>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-800 truncate">{scadenza.titolo}</h4>
                        {isScadenzaVicina(scadenza.giorno, scadenza.mese) && !isScadenzaPassata(scadenza.giorno, scadenza.mese) && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-medium rounded-full animate-pulse">
                            Urgente
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{scadenza.descrizione}</p>
                    </div>
                    
                    {/* Tipo Badge */}
                    <span className={`flex-shrink-0 px-3 py-1 text-xs font-medium rounded-full border ${getTipoColor(scadenza.tipo)}`}>
                      {getTipoLabel(scadenza.tipo)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-slate-600">Nessuna scadenza importante questo mese</p>
              </div>
            )}
          </div>

          {/* Legenda */}
          <div className="px-6 pb-6">
            <div className="flex flex-wrap gap-3 justify-center pt-4 border-t border-slate-100">
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span> IVA
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="w-3 h-3 rounded-full bg-red-500"></span> Imposte
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="w-3 h-3 rounded-full bg-green-500"></span> Contributi
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="w-3 h-3 rounded-full bg-purple-500"></span> Ritenute
              </span>
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="w-3 h-3 rounded-full bg-amber-500"></span> Dichiarazioni
              </span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm mb-3">Hai bisogno di assistenza per una scadenza?</p>
          <a href="#contatti" className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-medium rounded-full transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Prenota una consulenza
          </a>
        </div>
      </div>
    </section>
  );
};

// ==================== FAQ SECTION ====================
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      domanda: "Quali documenti devo portare per la dichiarazione dei redditi?",
      risposta: "Per la dichiarazione dei redditi sono necessari: CU (Certificazione Unica) del datore di lavoro, documentazione spese mediche e sanitarie, spese scolastiche/universitarie, interessi mutuo prima casa, spese ristrutturazione e risparmio energetico, ricevute di affitto, documentazione per detrazioni familiari a carico, eventuali altri redditi (affitti, collaborazioni, etc.)."
    },
    {
      domanda: "Quanto costa aprire una Partita IVA?",
      risposta: "L'apertura della Partita IVA di per sé è gratuita presso l'Agenzia delle Entrate. I costi da considerare sono: consulenza iniziale per la scelta del regime fiscale più conveniente, eventuale iscrizione alla Camera di Commercio (per attività commerciali), contributi INPS fissi trimestrali. Il nostro studio offre un pacchetto completo di apertura con assistenza nel primo anno."
    },
    {
      domanda: "Qual è la differenza tra regime forfettario e regime ordinario?",
      risposta: "Il regime forfettario prevede un'imposta sostitutiva del 15% (5% per i primi 5 anni di nuova attività), nessun obbligo di fatturazione elettronica sotto i 25.000€, e semplificazioni contabili. È accessibile con ricavi fino a 85.000€. Il regime ordinario invece prevede IRPEF a scaglioni, IVA, e contabilità completa, ma permette la deduzione analitica dei costi."
    },
    {
      domanda: "Ogni quanto devo versare i contributi INPS?",
      risposta: "I contributi INPS per artigiani e commercianti si versano trimestralmente (16 maggio, 20 agosto, 16 novembre, 16 febbraio). Per la Gestione Separata i versamenti seguono le scadenze delle imposte sui redditi (giugno/luglio e novembre). Per i dipendenti, il versamento è mensile entro il 16 del mese successivo."
    },
    {
      domanda: "Come funziona la fatturazione elettronica?",
      risposta: "La fatturazione elettronica è obbligatoria per tutti i soggetti IVA (con alcune eccezioni per i forfettari sotto i 25.000€). Le fatture vanno emesse in formato XML e trasmesse tramite il Sistema di Interscambio (SDI). Il nostro studio offre un servizio di gestione completa della fatturazione elettronica con software dedicato."
    },
    {
      domanda: "Quanto tempo ci vuole per avere il rimborso del 730?",
      risposta: "Se il 730 viene presentato entro maggio, il rimborso arriva generalmente con la busta paga di luglio. Per dichiarazioni presentate successivamente, i tempi si allungano. Se non si ha un sostituto d'imposta, l'Agenzia delle Entrate effettua il rimborso direttamente, con tempistiche più lunghe (anche 6-12 mesi)."
    },
    {
      domanda: "Cosa rischio se non pago le tasse in tempo?",
      risposta: "Il mancato pagamento comporta sanzioni e interessi: ravvedimento operoso entro 14 giorni (0,1% al giorno), entro 30 giorni (1,5%), entro 90 giorni (1,67%), entro 1 anno (3,75%), oltre 1 anno (4,29% + interessi legali). È sempre conveniente regolarizzare prima possibile con il ravvedimento operoso."
    },
    {
      domanda: "Come posso ridurre legalmente le tasse?",
      risposta: "Esistono diverse strategie di ottimizzazione fiscale legale: sfruttare tutte le detrazioni disponibili (19%), dedurre i costi effettivamente sostenuti per l'attività, contributi previdenziali integrativi, piani di welfare aziendale, scelta del regime fiscale più conveniente. Il nostro studio analizza ogni situazione per individuare la strategia migliore."
    },
  ];

  return (
    <section id="faq" className="py-20 bg-white relative overflow-hidden" data-testid="faq-section">
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-cyan-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 translate-y-1/2"></div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-4">
            Risposte Rapide
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            Domande Frequenti
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Trova risposte alle domande più comuni su tasse, contributi e adempimenti fiscali
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all hover:border-cyan-300 hover:shadow-md"
              data-testid={`faq-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
              >
                <span className="font-medium text-slate-800 pr-4">{faq.domanda}</span>
                <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  openIndex === index ? 'bg-cyan-500 rotate-180' : 'bg-slate-100'
                }`}>
                  <svg className={`w-4 h-4 ${openIndex === index ? 'text-white' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>
              <div className={`transition-all duration-300 ease-in-out ${
                openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              } overflow-hidden`}>
                <div className="px-6 pb-5">
                  <p className="text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                    {faq.risposta}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-semibold text-white mb-2">Non hai trovato la risposta?</h3>
          <p className="text-white/70 mb-6">Contattaci o chatta con Sara, la nostra assistente virtuale</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contatti" className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-medium rounded-full transition-colors">
              Contattaci
            </a>
            <button 
              onClick={() => document.querySelector('[data-testid="chatbot-toggle"]')?.click()}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full transition-colors border border-white/20"
            >
              Chatta con Sara
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== CALCOLATORE TASSE SECTION ====================
const CalcolatoreTasseSection = () => {
  const [tipoCalcolo, setTipoCalcolo] = useState('inps');
  const [fatturato, setFatturato] = useState('');
  const [risultato, setRisultato] = useState(null);

  // Coefficienti di redditività forfettario per categoria
  const coefficienti = {
    'commercio': { coeff: 40, descrizione: 'Commercio ingrosso/dettaglio' },
    'servizi': { coeff: 78, descrizione: 'Attività professionali e servizi' },
    'artigianato': { coeff: 67, descrizione: 'Costruzioni e attività manifatturiere' },
    'alimentari': { coeff: 40, descrizione: 'Commercio alimenti e bevande' },
    'ambulante': { coeff: 54, descrizione: 'Commercio ambulante alimentari' },
    'intermediari': { coeff: 62, descrizione: 'Intermediari del commercio' },
    'affitto': { coeff: 86, descrizione: 'Affitto immobili' },
    'altro': { coeff: 67, descrizione: 'Altre attività economiche' },
  };

  const [attivita, setAttivita] = useState('servizi');
  const [nuovaAttivita, setNuovaAttivita] = useState(false);

  const calcolaForfettario = () => {
    const fatt = parseFloat(fatturato) || 0;
    if (fatt <= 0) {
      setRisultato(null);
      return;
    }

    const coeff = coefficienti[attivita].coeff;
    const redditoImponibile = fatt * (coeff / 100);
    const aliquotaImposta = nuovaAttivita ? 5 : 15;
    const impostaSostitutiva = redditoImponibile * (aliquotaImposta / 100);
    
    // INPS Gestione Separata (circa 26.23% sul reddito)
    const contributiINPS = redditoImponibile * 0.2623;
    
    const totaleCarico = impostaSostitutiva + contributiINPS;
    const nettoStimato = fatt - totaleCarico;

    setRisultato({
      fatturato: fatt,
      coefficiente: coeff,
      redditoImponibile: redditoImponibile,
      aliquotaImposta: aliquotaImposta,
      impostaSostitutiva: impostaSostitutiva,
      contributiINPS: contributiINPS,
      totaleCarico: totaleCarico,
      nettoStimato: nettoStimato,
      percentualeCarico: (totaleCarico / fatt * 100).toFixed(1)
    });
  };

  const calcolaINPS = () => {
    const reddito = parseFloat(fatturato) || 0;
    if (reddito <= 0) {
      setRisultato(null);
      return;
    }

    // Gestione Separata 2024
    const aliquotaGS = 26.23;
    const contributoGS = reddito * (aliquotaGS / 100);
    
    // Artigiani/Commercianti - contributo fisso + percentuale
    const contributoFissoAC = 4427.04; // contributo minimo annuo 2024
    const redditoMinimale = 18415;
    const redditoEccedente = Math.max(0, reddito - redditoMinimale);
    const contributoPercentualeAC = redditoEccedente * 0.2472;
    const contributoTotaleAC = contributoFissoAC + contributoPercentualeAC;

    setRisultato({
      reddito: reddito,
      gestioneSeparata: {
        aliquota: aliquotaGS,
        contributo: contributoGS,
        mensile: contributoGS / 12
      },
      artigianiCommercianti: {
        fisso: contributoFissoAC,
        percentuale: contributoPercentualeAC,
        totale: contributoTotaleAC,
        trimestrale: contributoTotaleAC / 4
      }
    });
  };

  const eseguiCalcolo = () => {
    if (tipoCalcolo === 'inps') {
      calcolaINPS();
    } else {
      calcolaForfettario();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <section id="calcolatore" className="py-20 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden" data-testid="calcolatore-section">
      <div className="absolute top-0 left-0 w-72 h-72 bg-green-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 text-sm font-medium rounded-full mb-4">
            Strumento Gratuito
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            Calcolatore Tasse e Contributi
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Stima rapidamente imposte e contributi INPS. I risultati sono indicativi - contattaci per un calcolo preciso.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form Input */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
            {/* Tipo Calcolo Toggle */}
            <div className="flex bg-slate-100 rounded-xl p-1 mb-6">
              <button
                onClick={() => { setTipoCalcolo('inps'); setRisultato(null); }}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                  tipoCalcolo === 'inps' 
                    ? 'bg-white text-slate-800 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Contributi INPS
              </button>
              <button
                onClick={() => { setTipoCalcolo('forfettario'); setRisultato(null); }}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                  tipoCalcolo === 'forfettario' 
                    ? 'bg-white text-slate-800 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Regime Forfettario
              </button>
            </div>

            {/* Input Fields */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tipoCalcolo === 'inps' ? 'Reddito Annuo Lordo (€)' : 'Fatturato Annuo (€)'}
                </label>
                <input
                  type="number"
                  value={fatturato}
                  onChange={(e) => setFatturato(e.target.value)}
                  placeholder="Es. 30000"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                />
              </div>

              {tipoCalcolo === 'forfettario' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Tipo di Attività
                    </label>
                    <select
                      value={attivita}
                      onChange={(e) => setAttivita(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                    >
                      {Object.entries(coefficienti).map(([key, val]) => (
                        <option key={key} value={key}>{val.descrizione} ({val.coeff}%)</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <input
                      type="checkbox"
                      id="nuovaAttivita"
                      checked={nuovaAttivita}
                      onChange={(e) => setNuovaAttivita(e.target.checked)}
                      className="w-5 h-5 text-cyan-500 border-slate-300 rounded focus:ring-cyan-500"
                    />
                    <label htmlFor="nuovaAttivita" className="text-sm text-slate-700">
                      <span className="font-medium">Nuova attività</span>
                      <span className="text-slate-500"> (aliquota ridotta 5% per i primi 5 anni)</span>
                    </label>
                  </div>
                </>
              )}

              <button
                onClick={eseguiCalcolo}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                Calcola
              </button>
            </div>

            {/* Disclaimer */}
            <p className="mt-4 text-xs text-slate-400 text-center">
              * Calcolo indicativo basato sui parametri 2024. Per un'analisi precisa della tua situazione, contatta il nostro studio.
            </p>
          </div>

          {/* Risultati */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-lg p-6 md:p-8 text-white">
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Risultato Calcolo
            </h3>

            {risultato ? (
              tipoCalcolo === 'inps' ? (
                <div className="space-y-6">
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-white/60 text-sm mb-1">Reddito inserito</p>
                    <p className="text-2xl font-bold">{formatCurrency(risultato.reddito)}</p>
                  </div>

                  <div>
                    <h4 className="text-cyan-400 font-medium mb-3">Gestione Separata</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-white/70">Aliquota</span>
                        <span>{risultato.gestioneSeparata.aliquota}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Contributo annuo</span>
                        <span className="font-semibold">{formatCurrency(risultato.gestioneSeparata.contributo)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Equivalente mensile</span>
                        <span className="text-white/70">{formatCurrency(risultato.gestioneSeparata.mensile)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <h4 className="text-amber-400 font-medium mb-3">Artigiani / Commercianti</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-white/70">Contributo fisso</span>
                        <span>{formatCurrency(risultato.artigianiCommercianti.fisso)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Contributo percentuale</span>
                        <span>{formatCurrency(risultato.artigianiCommercianti.percentuale)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Totale annuo</span>
                        <span>{formatCurrency(risultato.artigianiCommercianti.totale)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Rata trimestrale</span>
                        <span className="text-white/70">{formatCurrency(risultato.artigianiCommercianti.trimestrale)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <p className="text-white/60 text-xs mb-1">Fatturato</p>
                      <p className="text-lg font-bold">{formatCurrency(risultato.fatturato)}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <p className="text-white/60 text-xs mb-1">Coefficiente</p>
                      <p className="text-lg font-bold">{risultato.coefficiente}%</p>
                    </div>
                  </div>

                  <div className="p-4 bg-cyan-500/20 rounded-xl border border-cyan-500/30">
                    <p className="text-cyan-300 text-sm mb-1">Reddito Imponibile</p>
                    <p className="text-xl font-bold">{formatCurrency(risultato.redditoImponibile)}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/70">Imposta ({risultato.aliquotaImposta}%)</span>
                      <span>{formatCurrency(risultato.impostaSostitutiva)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Contributi INPS</span>
                      <span>{formatCurrency(risultato.contributiINPS)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-white/10">
                      <span className="font-medium">Totale carico fiscale</span>
                      <span className="font-bold text-red-400">{formatCurrency(risultato.totaleCarico)}</span>
                    </div>
                  </div>

                  <div className="p-4 bg-green-500/20 rounded-xl border border-green-500/30 mt-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-green-300 text-sm">Netto Stimato</p>
                        <p className="text-2xl font-bold text-green-400">{formatCurrency(risultato.nettoStimato)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/50 text-xs">Carico fiscale</p>
                        <p className="text-lg font-semibold">{risultato.percentualeCarico}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-white/50">Inserisci i dati e clicca su "Calcola" per vedere i risultati</p>
              </div>
            )}

            {risultato && (
              <div className="mt-6 pt-4 border-t border-white/10">
                <a 
                  href="#contatti" 
                  className="block w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-center font-medium rounded-xl transition-colors"
                >
                  Richiedi consulenza personalizzata
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== CONTACT SECTION ====================
const ContactSection = ({ onOpenPrivacy }) => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefono: "",
    messaggio: "",
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      alert("Devi accettare l'informativa sulla privacy per inviare il messaggio.");
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success");
      setFormData({ nome: "", email: "", telefono: "", messaggio: "" });
      setPrivacyAccepted(false);
    } catch (error) {
      console.error("Errore invio form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailClick = (e, user, domain) => {
    e.preventDefault();
    window.location.href = `mailto:${user}@${domain}`;
  };

  const contactInfo = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Indirizzo",
      lines: ["Via Umberto I° 19", "33097 Spilimbergo (PN)"],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: "Telefono",
      lines: ["+39 0427 51251"],
      link: "tel:+390427512 51",
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "Email",
      emailUser: "info",
      emailDomain: "consulenze-aziendali.it",
      isEmail: true,
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "PEC",
      emailUser: "consaz",
      emailDomain: "legalmail.it",
      isEmail: true,
    },
  ];

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="py-24 bg-indigo-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 font-semibold text-sm rounded-full mb-4">
            Contattaci
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Iniziamo Insieme il Percorso verso il Successo
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Saremo lieti di fissare un primo incontro conoscitivo senza impegno
            per valutare insieme le esigenze della vostra attività.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Dove Trovarci
            </h3>
            <p className="text-gray-600 mb-8">
              Il nostro studio è a vostra disposizione per qualsiasi esigenza.
              Contattateci per fissare un appuntamento.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-5 border border-indigo-100 hover:border-indigo-300 hover:shadow-lg transition-all"
                >
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 mb-3">
                    {info.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm">{info.title}</h4>
                  {info.isEmail ? (
                    <button
                      onClick={(e) => handleEmailClick(e, info.emailUser, info.emailDomain)}
                      className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 cursor-pointer text-sm"
                    >
                      <span className="hover:underline">Invia email</span>
                    </button>
                  ) : info.link ? (
                    <a
                      href={info.link}
                      className="block text-indigo-600 hover:underline text-sm"
                    >
                      {info.lines[0]}
                    </a>
                  ) : (
                    info.lines.map((line, i) => (
                      <p key={i} className="text-gray-600 text-sm">
                        {line}
                      </p>
                    ))
                  )}
                </div>
              ))}
            </div>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/39042751251"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-8 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors shadow-md hover:shadow-lg"
              data-testid="whatsapp-btn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Contattaci su WhatsApp
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="mb-5">
                <label className="block text-slate-700 font-medium mb-2 text-sm">
                  Nome e Cognome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) =>
                    setFormData({ ...formData, nome: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                  placeholder="Il tuo nome"
                  data-testid="input-nome"
                />
              </div>

              <div className="mb-5">
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                  placeholder="La tua email"
                  data-testid="input-email"
                />
              </div>

              <div className="mb-5">
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) =>
                    setFormData({ ...formData, telefono: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                  placeholder="Il tuo numero"
                  data-testid="input-telefono"
                />
              </div>

              <div className="mb-5">
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  Messaggio *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.messaggio}
                  onChange={(e) =>
                    setFormData({ ...formData, messaggio: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                  placeholder="Il tuo messaggio"
                  data-testid="input-messaggio"
                />
              </div>

              {/* Privacy Consent Checkbox */}
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="mt-1 w-5 h-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                    data-testid="privacy-checkbox"
                  />
                  <span className="text-sm text-gray-600">
                    Ho letto e accetto l'
                    <button 
                      type="button"
                      onClick={onOpenPrivacy}
                      className="text-indigo-600 hover:underline font-medium"
                    >
                      Informativa sulla Privacy
                    </button>
                    {" "}e acconsento al trattamento dei miei dati personali per rispondere alla mia richiesta. *
                  </span>
                </label>
              </div>

              {submitStatus === "success" && (
                <div className="mb-6 p-4 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200 text-sm">
                  Messaggio inviato con successo! Ti ricontatteremo presto.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 text-sm">
                  Errore nell'invio. Riprova o contattaci telefonicamente.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !privacyAccepted}
                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl"
                data-testid="submit-btn"
              >
                {isSubmitting ? "Invio in corso..." : "Invia Richiesta"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== AI CHATBOT ====================
const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState({ nome: "", cognome: "", azienda: "", codice_fiscale_piva: "", email: "" });
  const [recoveryStep, setRecoveryStep] = useState(0);
  
  // Stato per richiesta documenti
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [documentForm, setDocumentForm] = useState({ email: "", cf_piva: "", documento: "" });
  const [documentResults, setDocumentResults] = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  
  // Stato per verifica OTP/PIN
  const [documentStep, setDocumentStep] = useState(1); // 1=dati, 2=scelta metodo, 3=verifica OTP/PIN, 4=documenti
  const [verificationMethod, setVerificationMethod] = useState(null); // 'otp' o 'pin'
  const [otpCode, setOtpCode] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [downloadToken, setDownloadToken] = useState(null);
  const [clienteInfo, setClienteInfo] = useState(null);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Funzione per gestire il recupero credenziali
  const handleRecoverySubmit = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/recupera-credenziali`, recoveryForm);
      setMessages([
        ...messages,
        { role: "assistant", content: response.data.message }
      ]);
      setShowRecoveryForm(false);
      setRecoveryForm({ nome: "", cognome: "", azienda: "", codice_fiscale_piva: "", email: "" });
      setRecoveryStep(0);
    } catch (error) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Errore durante il recupero. Contatta lo studio al +39 0427 51251." }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 1: Verifica cliente (email + CF/PIVA)
  const handleVerifyClient = async () => {
    if (!documentForm.email || !documentForm.cf_piva) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chatbot/verifica-cliente`, {
        email: documentForm.email,
        cf_piva: documentForm.cf_piva
      });
      
      if (response.data.verificato) {
        setClienteInfo(response.data);
        setDocumentStep(2); // Vai a scelta metodo
        setMessages([
          ...messages,
          { role: "assistant", content: `Ciao ${response.data.nome}! Per sicurezza, scegli come verificare la tua identità.` }
        ]);
      } else {
        setMessages([
          ...messages,
          { role: "assistant", content: response.data.messaggio || "Dati non validi." }
        ]);
      }
    } catch (error) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Errore nella verifica. Riprova." }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2: Invia OTP
  const handleSendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chatbot/invia-otp`, {
        email: documentForm.email,
        cf_piva: documentForm.cf_piva
      });
      
      if (response.data.success) {
        setVerificationMethod('otp');
        setDocumentStep(3);
        setMessages([
          ...messages,
          { role: "assistant", content: `📧 ${response.data.messaggio}` }
        ]);
      } else {
        setMessages([
          ...messages,
          { role: "assistant", content: response.data.messaggio }
        ]);
      }
    } catch (error) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Errore nell'invio OTP. Riprova." }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3a: Verifica OTP
  const handleVerifyOTP = async () => {
    if (!otpCode) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chatbot/verifica-otp`, {
        email: documentForm.email,
        otp: otpCode
      });
      
      if (response.data.verificato) {
        setDownloadToken(response.data.download_token);
        setDocumentStep(4);
        setMessages([
          ...messages,
          { role: "assistant", content: "✅ Verifica completata! Ora cerca il documento." }
        ]);
      } else {
        setMessages([
          ...messages,
          { role: "assistant", content: response.data.messaggio }
        ]);
      }
    } catch (error) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Errore nella verifica OTP." }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3b: Verifica PIN
  const handleVerifyPIN = async () => {
    if (!pinCode) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chatbot/verifica-pin`, {
        email: documentForm.email,
        cf_piva: documentForm.cf_piva,
        pin: pinCode
      });
      
      if (response.data.verificato) {
        setDownloadToken(response.data.download_token);
        setDocumentStep(4);
        setMessages([
          ...messages,
          { role: "assistant", content: "✅ PIN verificato! Ora cerca il documento." }
        ]);
      } else {
        setMessages([
          ...messages,
          { role: "assistant", content: response.data.messaggio }
        ]);
      }
    } catch (error) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Errore nella verifica PIN." }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 4: Cerca documento
  const handleDocumentSearch = async () => {
    if (!documentForm.documento || !downloadToken) return;
    
    setIsLoading(true);
    setDocumentResults(null);
    
    try {
      const response = await axios.post(`${API}/chatbot/cerca-documento`, documentForm);
      
      if (response.data.trovato && response.data.documenti?.length > 0) {
        setDocumentResults({
          success: true,
          documenti: response.data.documenti,
          messaggio: response.data.messaggio
        });
        setMessages([
          ...messages,
          { role: "assistant", content: `✅ ${response.data.messaggio}` }
        ]);
      } else {
        setMessages([
          ...messages,
          { role: "assistant", content: response.data.messaggio || "Nessun documento trovato." }
        ]);
      }
    } catch (error) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Errore nella ricerca. Riprova." }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Download documento (con token)
  const handleDocumentDownload = async (doc) => {
    if (!downloadToken) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Sessione scaduta. Verifica nuovamente la tua identità." }
      ]);
      return;
    }
    
    setDownloadingDocId(doc.id);
    try {
      const response = await axios.get(
        `${API}/chatbot/download/${doc.id}?email=${encodeURIComponent(documentForm.email)}&download_token=${encodeURIComponent(downloadToken)}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nome);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessages([
        ...messages,
        { role: "assistant", content: `✅ Download di "${doc.nome}" completato!` }
      ]);
    } catch (error) {
      setMessages([
        ...messages,
        { role: "assistant", content: "Errore nel download. La sessione potrebbe essere scaduta." }
      ]);
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Chiudi form documenti e reset
  const closeDocumentForm = () => {
    setShowDocumentForm(false);
    setDocumentForm({ email: "", cf_piva: "", documento: "" });
    setDocumentResults(null);
    setDocumentStep(1);
    setVerificationMethod(null);
    setOtpCode("");
    setPinCode("");
    setDownloadToken(null);
    setClienteInfo(null);
  };

  // Scegli metodo PIN
  const selectPINMethod = () => {
    setVerificationMethod('pin');
    setDocumentStep(3);
  };

  // Verifica se l'utente vuole recuperare le credenziali
  const checkForRecoveryIntent = (message) => {
    const recoveryKeywords = ["password", "credenziali", "non ricordo", "dimenticato", "accesso", "login", "non riesco ad accedere", "recupero", "recuperare"];
    const lowerMsg = message.toLowerCase();
    return recoveryKeywords.some(kw => lowerMsg.includes(kw));
  };

  // Verifica se l'utente vuole scaricare un documento
  const checkForDocumentIntent = (message) => {
    const documentKeywords = ["documento", "documenti", "dichiarazione", "redditi", "f24", "bilancio", "fattura", "cedolino", "busta paga", "scaricare", "scarica", "download", "pdf", "modello"];
    const lowerMsg = message.toLowerCase();
    return documentKeywords.some(kw => lowerMsg.includes(kw));
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    
    // Controlla se l'utente vuole recuperare credenziali
    if (checkForRecoveryIntent(userMessage) && !showRecoveryForm && !showDocumentForm) {
      setMessages([
        ...messages,
        { role: "user", content: userMessage },
        { role: "assistant", content: "Capisco che hai bisogno di recuperare le credenziali per l'Area Clienti. Per verificare la tua identità, compila il form qui sotto con i tuoi dati." }
      ]);
      setShowRecoveryForm(true);
      return;
    }

    // Controlla se l'utente vuole scaricare un documento
    if (checkForDocumentIntent(userMessage) && !showDocumentForm && !showRecoveryForm) {
      setMessages([
        ...messages,
        { role: "user", content: userMessage },
        { role: "assistant", content: "Certamente! Per recuperare il documento ho bisogno di verificare la tua identità. Compila il form qui sotto." }
      ]);
      setShowDocumentForm(true);
      // Pre-compila il campo documento con la richiesta dell'utente
      setDocumentForm({ ...documentForm, documento: userMessage });
      return;
    }

    setIsLoading(true);

    const newMessages = [
      ...messages,
      { role: "user", content: userMessage },
    ];
    setMessages(newMessages);

    try {
      const response = await axios.post(`${API}/chat`, {
        messages: messages,
        user_message: userMessage,
      });

      setMessages(response.data.messages);
    } catch (error) {
      console.error("Errore chatbot:", error);
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content:
            "Mi dispiace, si è verificato un errore. Ti prego di riprovare o contattare lo studio direttamente al numero +39 0427 51251.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/39042751251"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-24 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
        data-testid="whatsapp-floating-btn"
        title="Contattaci su WhatsApp"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
        data-testid="chatbot-toggle"
      >
        {isOpen ? (
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden border border-indigo-200"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold">Sara - Studio Grassetti</h3>
                <p className="text-indigo-200 text-sm">Sono qui per aiutarLa</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <div className="w-12 h-12 mx-auto bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </div>
                <p className="font-medium text-gray-700">Buongiorno! Sono Sara, come posso aiutarLa?</p>
                <p className="text-sm mt-2 text-gray-500">
                  Mi chieda pure informazioni sui nostri servizi, scadenze fiscali, o
                  qualsiasi altra domanda.
                </p>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === "user"
                      ? "bg-indigo-600 text-white rounded-br-md"
                      : "bg-white text-gray-800 shadow-sm border border-gray-200 rounded-bl-md"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-200 rounded-bl-md">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" />
                    <div
                      className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    />
                    <div
                      className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Form Recupero Credenziali */}
            {showRecoveryForm && (
              <div className="bg-white p-4 rounded-xl border border-cyan-200 shadow-sm">
                <h4 className="font-semibold text-slate-800 mb-1 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Recupero Credenziali
                </h4>
                <p className="text-xs text-slate-500 mb-3">Compila tutti i campi per verificare la tua identità</p>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Nome *"
                      value={recoveryForm.nome}
                      onChange={(e) => setRecoveryForm({...recoveryForm, nome: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    />
                    <input
                      type="text"
                      placeholder="Cognome *"
                      value={recoveryForm.cognome}
                      onChange={(e) => setRecoveryForm({...recoveryForm, cognome: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Azienda (o 'privato' se persona fisica)"
                    value={recoveryForm.azienda}
                    onChange={(e) => setRecoveryForm({...recoveryForm, azienda: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  <input
                    type="text"
                    placeholder="Codice Fiscale o Partita IVA *"
                    value={recoveryForm.codice_fiscale_piva}
                    onChange={(e) => setRecoveryForm({...recoveryForm, codice_fiscale_piva: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 uppercase"
                  />
                  <input
                    type="email"
                    placeholder="Email registrata *"
                    value={recoveryForm.email}
                    onChange={(e) => setRecoveryForm({...recoveryForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => {
                        setShowRecoveryForm(false);
                        setRecoveryForm({ nome: "", cognome: "", azienda: "", codice_fiscale_piva: "", email: "" });
                      }}
                      className="flex-1 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={handleRecoverySubmit}
                      disabled={!recoveryForm.nome || !recoveryForm.cognome || !recoveryForm.codice_fiscale_piva || !recoveryForm.email || isLoading}
                      className="flex-1 px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm hover:bg-cyan-700 disabled:bg-slate-400 transition-colors flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Verifica...
                        </>
                      ) : "Recupera Credenziali"}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-2">
                    I dati verranno verificati con quelli registrati nel nostro sistema
                  </p>
                </div>
              </div>
            )}

            {/* Form Richiesta Documenti - Flusso a 4 Step */}
            {showDocumentForm && (
              <div className="bg-white p-4 rounded-xl border border-green-200 shadow-sm">
                <h4 className="font-semibold text-slate-800 mb-1 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Richiesta Documento Sicura
                </h4>
                
                {/* Progress indicator */}
                <div className="flex items-center gap-1 mb-3 mt-2">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        documentStep >= step 
                          ? 'bg-green-500 text-white' 
                          : 'bg-slate-200 text-slate-500'
                      }`}>
                        {documentStep > step ? '✓' : step}
                      </div>
                      {step < 4 && (
                        <div className={`w-4 h-0.5 ${documentStep > step ? 'bg-green-500' : 'bg-slate-200'}`}></div>
                      )}
                    </div>
                  ))}
                  <span className="text-xs text-slate-500 ml-2">
                    {documentStep === 1 && "Identificazione"}
                    {documentStep === 2 && "Scelta verifica"}
                    {documentStep === 3 && "Verifica"}
                    {documentStep === 4 && "Documenti"}
                  </span>
                </div>

                {/* STEP 1: Email + CF/PIVA */}
                {documentStep === 1 && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 mb-2">Inserisci i tuoi dati per verificare l'identità</p>
                    <input
                      type="email"
                      placeholder="Email registrata *"
                      value={documentForm.email}
                      onChange={(e) => setDocumentForm({...documentForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="doc-email-input"
                    />
                    <input
                      type="text"
                      placeholder="Codice Fiscale o Partita IVA *"
                      value={documentForm.cf_piva}
                      onChange={(e) => setDocumentForm({...documentForm, cf_piva: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 uppercase"
                      data-testid="doc-cfpiva-input"
                    />
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={closeDocumentForm}
                        className="flex-1 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                        data-testid="doc-cancel-btn"
                      >
                        Annulla
                      </button>
                      <button
                        onClick={handleVerifyClient}
                        disabled={!documentForm.email || !documentForm.cf_piva || isLoading}
                        className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:bg-slate-400 transition-colors flex items-center justify-center gap-2"
                        data-testid="doc-verify-btn"
                      >
                        {isLoading ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Verifica...
                          </>
                        ) : "Continua →"}
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 2: Scelta metodo OTP o PIN */}
                {documentStep === 2 && clienteInfo && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-600 mb-2">
                      Ciao <span className="font-semibold">{clienteInfo.nome}</span>! Come preferisci verificare la tua identità?
                    </p>
                    <button
                      onClick={handleSendOTP}
                      disabled={isLoading}
                      className="w-full p-3 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left flex items-center gap-3"
                      data-testid="doc-otp-method-btn"
                    >
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-800">📧 Codice via Email (OTP)</p>
                        <p className="text-xs text-slate-500">Ricevi un codice a 6 cifre via email</p>
                      </div>
                      {isLoading && verificationMethod === null && (
                        <svg className="w-4 h-4 animate-spin ml-auto text-green-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </button>
                    
                    {clienteInfo.ha_pin && (
                      <button
                        onClick={selectPINMethod}
                        className="w-full p-3 border-2 border-cyan-200 rounded-lg hover:border-cyan-400 hover:bg-cyan-50 transition-colors text-left flex items-center gap-3"
                        data-testid="doc-pin-method-btn"
                      >
                        <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-800">🔐 PIN Personale</p>
                          <p className="text-xs text-slate-500">Usa il tuo PIN segreto</p>
                        </div>
                      </button>
                    )}
                    
                    <button
                      onClick={() => setDocumentStep(1)}
                      className="w-full px-3 py-2 text-slate-500 text-sm hover:text-slate-700 transition-colors"
                      data-testid="doc-back-step1-btn"
                    >
                      ← Torna indietro
                    </button>
                  </div>
                )}

                {/* STEP 3: Inserimento OTP o PIN */}
                {documentStep === 3 && (
                  <div className="space-y-3">
                    {verificationMethod === 'otp' ? (
                      <>
                        <p className="text-xs text-slate-600 mb-2">
                          Abbiamo inviato un codice a <span className="font-semibold">{documentForm.email}</span>
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Inserisci codice OTP"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm text-center text-lg tracking-widest font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            maxLength={6}
                            data-testid="doc-otp-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setDocumentStep(2);
                              setOtpCode("");
                              setVerificationMethod(null);
                            }}
                            className="flex-1 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                          >
                            ← Indietro
                          </button>
                          <button
                            onClick={handleVerifyOTP}
                            disabled={otpCode.length !== 6 || isLoading}
                            className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:bg-slate-400 transition-colors flex items-center justify-center gap-2"
                            data-testid="doc-verify-otp-btn"
                          >
                            {isLoading ? (
                              <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Verifica...
                              </>
                            ) : "Verifica OTP"}
                          </button>
                        </div>
                        <button
                          onClick={handleSendOTP}
                          disabled={isLoading}
                          className="w-full text-xs text-cyan-600 hover:text-cyan-700 transition-colors"
                        >
                          Non hai ricevuto il codice? Invia di nuovo
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-slate-600 mb-2">
                          Inserisci il tuo PIN personale
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="password"
                            placeholder="PIN"
                            value={pinCode}
                            onChange={(e) => setPinCode(e.target.value)}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm text-center text-lg tracking-widest font-mono focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                            data-testid="doc-pin-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setDocumentStep(2);
                              setPinCode("");
                              setVerificationMethod(null);
                            }}
                            className="flex-1 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                          >
                            ← Indietro
                          </button>
                          <button
                            onClick={handleVerifyPIN}
                            disabled={!pinCode || isLoading}
                            className="flex-1 px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm hover:bg-cyan-700 disabled:bg-slate-400 transition-colors flex items-center justify-center gap-2"
                            data-testid="doc-verify-pin-btn"
                          >
                            {isLoading ? (
                              <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Verifica...
                              </>
                            ) : "Verifica PIN"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* STEP 4: Ricerca e Download Documenti */}
                {documentStep === 4 && (
                  <div className="space-y-3">
                    <div className="p-2 bg-green-50 rounded-lg border border-green-200 mb-2">
                      <p className="text-xs text-green-700 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Identità verificata! Ora puoi cercare i tuoi documenti.
                      </p>
                    </div>
                    
                    {!documentResults ? (
                      <>
                        <input
                          type="text"
                          placeholder="Cerca documento (es. dichiarazione 2024, F24...)"
                          value={documentForm.documento}
                          onChange={(e) => setDocumentForm({...documentForm, documento: e.target.value})}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          data-testid="doc-search-input"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={closeDocumentForm}
                            className="flex-1 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                          >
                            Chiudi
                          </button>
                          <button
                            onClick={handleDocumentSearch}
                            disabled={!documentForm.documento || isLoading}
                            className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:bg-slate-400 transition-colors flex items-center justify-center gap-2"
                            data-testid="doc-search-btn"
                          >
                            {isLoading ? (
                              <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Ricerca...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Cerca
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        {documentResults.documenti && documentResults.documenti.length > 0 ? (
                          <>
                            <p className="text-xs text-slate-600 mb-2">
                              Trovati {documentResults.documenti.length} documento/i:
                            </p>
                            {documentResults.documenti.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-xl">📄</span>
                                  <div className="min-w-0">
                                    <p className="font-medium text-sm text-slate-800 truncate">{doc.nome}</p>
                                    <p className="text-xs text-slate-500">{doc.cartella}</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDocumentDownload(doc)}
                                  disabled={downloadingDocId === doc.id}
                                  className="flex-shrink-0 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white text-xs rounded-lg transition-colors flex items-center gap-1"
                                  data-testid={`doc-download-btn-${doc.id}`}
                                >
                                  {downloadingDocId === doc.id ? (
                                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <>
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                      Scarica
                                    </>
                                  )}
                                </button>
                              </div>
                            ))}
                          </>
                        ) : (
                          <p className="text-sm text-slate-600 text-center py-4">
                            Nessun documento trovato con questo nome.
                          </p>
                        )}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => {
                              setDocumentResults(null);
                              setDocumentForm({...documentForm, documento: ""});
                            }}
                            className="flex-1 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                          >
                            Nuova Ricerca
                          </button>
                          <button
                            onClick={closeDocumentForm}
                            className="flex-1 px-3 py-2 bg-slate-600 text-white rounded-lg text-sm hover:bg-slate-700 transition-colors"
                          >
                            Chiudi
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi un messaggio..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="w-10 h-10 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-full flex items-center justify-center transition-colors"
                data-testid="chatbot-send"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== FOOTER ====================
const Footer = ({ onOpenPrivacy, onOpenCookie }) => {
  const handleEmailClick = (e, user, domain) => {
    e.preventDefault();
    window.location.href = `mailto:${user}@${domain}`;
  };

  const openCookieSettings = () => {
    localStorage.removeItem("cookieConsent");
    window.location.reload();
  };

  return (
    <footer className="bg-gradient-to-b from-slate-900 via-blue-950 to-slate-950 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-500 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Logo e Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold">Studio Grassetti</h3>
                <p className="text-cyan-400 text-xs">Dal 1993</p>
              </div>
            </div>
            <p className="text-blue-200/70 text-sm mb-4">
              Commercialista e Revisore Contabile al servizio di imprese e professionisti.
            </p>
            <div className="flex gap-3">
              <a href="https://wa.me/39042751251" target="_blank" rel="noopener noreferrer" 
                className="w-10 h-10 bg-white/10 hover:bg-green-500 rounded-lg flex items-center justify-center transition-all hover:scale-110">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
              <a href="tel:+39042751251" 
                className="w-10 h-10 bg-white/10 hover:bg-cyan-500 rounded-lg flex items-center justify-center transition-all hover:scale-110">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </a>
              <button
                onClick={(e) => handleEmailClick(e, "info", "consulenze-aziendali.it")}
                className="w-10 h-10 bg-white/10 hover:bg-blue-500 rounded-lg flex items-center justify-center transition-all hover:scale-110">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Servizi */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded"></span>
              Servizi
            </h4>
            <ul className="space-y-3">
              <li><a href="#servizi" className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors hover:translate-x-1 inline-block">Consulenza Fiscale</a></li>
              <li><a href="#servizi" className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors hover:translate-x-1 inline-block">Consulenza Societaria</a></li>
              <li><a href="#servizi" className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors hover:translate-x-1 inline-block">Consulenza del Lavoro</a></li>
              <li><a href="#servizi" className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors hover:translate-x-1 inline-block">Servizi Amministrativi</a></li>
            </ul>
          </div>

          {/* Contatti */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded"></span>
              Contatti
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-blue-200/70 text-sm">Via Umberto I° 19<br/>33097 Spilimbergo (PN)</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-blue-200/70 text-sm">+39 0427 51251</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <button onClick={(e) => handleEmailClick(e, "info", "consulenze-aziendali.it")} className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors">
                  <span>info</span><span>@</span><span>consulenze-aziendali</span><span>.</span><span>it</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Legal & Privacy */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded"></span>
              Privacy & Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <button onClick={onOpenPrivacy} className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Privacy Policy
                </button>
              </li>
              <li>
                <button onClick={onOpenCookie} className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Cookie Policy
                </button>
              </li>
              <li>
                <button onClick={openCookieSettings} className="text-blue-200/70 hover:text-cyan-400 text-sm transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Gestisci Cookie
                </button>
              </li>
            </ul>
            <div className="mt-6 p-3 bg-white/5 rounded-lg border border-cyan-500/20">
              <p className="text-xs text-blue-300/60">
                P.IVA: IT01484710932<br/>
                C.F.: GRSLRT63S03I904B
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-cyan-500/20 pt-8 pb-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-blue-300/60 text-sm">
              © {new Date().getFullYear()} Studio Dott. Alberto Grassetti. Tutti i diritti riservati.
            </p>
            <a
              href="https://telcavoip.ai/it/services/webdesign"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-cyan-500/20 border border-cyan-500/20 hover:border-cyan-500/40 text-blue-200/70 hover:text-white text-xs rounded-full transition-all"
            >
              <span>Powered with</span>
              <span className="text-red-400">❤️</span>
              <span>by</span>
              <span className="font-semibold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">TelcaVoIP AI</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== GDPR COOKIE BANNER ====================
const CookieBanner = ({ onOpenPrivacy, onOpenCookie }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Sempre attivi
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem("cookieConsent", JSON.stringify(prefs));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    savePreferences({ necessary: true, analytics: true, marketing: true });
  };

  const rejectAll = () => {
    savePreferences({ necessary: true, analytics: false, marketing: false });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      {!showPreferences && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t border-slate-200" data-testid="cookie-banner">
          <div className="max-w-7xl mx-auto p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 text-lg mb-2">
                  Informativa sui Cookie
                </h3>
                <p className="text-slate-600 text-sm mb-3">
                  Questo sito utilizza cookie tecnici necessari al funzionamento e cookie di terze parti per 
                  finalità analitiche. Puoi accettare tutti i cookie, rifiutare quelli non necessari o 
                  personalizzare le tue preferenze.
                </p>
                <div className="flex gap-4 text-sm">
                  <button 
                    onClick={onOpenPrivacy}
                    className="text-blue-700 hover:underline"
                  >
                    Privacy Policy
                  </button>
                  <button 
                    onClick={onOpenCookie}
                    className="text-blue-700 hover:underline"
                  >
                    Cookie Policy
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:items-center">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors"
                >
                  Personalizza
                </button>
                <button
                  onClick={rejectAll}
                  className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors"
                >
                  Rifiuta Non Necessari
                </button>
                <button
                  onClick={acceptAll}
                  className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-lg text-sm transition-colors"
                  data-testid="accept-cookies"
                >
                  Accetta Tutti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">Preferenze Cookie</h2>
              <p className="text-slate-600 text-sm mt-1">
                Gestisci le tue preferenze sui cookie. I cookie necessari non possono essere disattivati.
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Cookie Necessari */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      Cookie Necessari
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Sempre attivi</span>
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      Essenziali per il funzionamento del sito. Includono cookie per la sessione, 
                      le preferenze di consenso e la sicurezza. Non possono essere disattivati.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-emerald-500 rounded-full relative">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cookie Analitici */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">Cookie Analitici</h3>
                    <p className="text-slate-600 text-sm mt-1">
                      Ci aiutano a capire come i visitatori interagiscono con il sito, raccogliendo 
                      informazioni in forma anonima. Utilizziamo questi dati per migliorare il sito.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences({...preferences, analytics: !preferences.analytics})}
                      className={`w-12 h-6 rounded-full relative transition-colors ${preferences.analytics ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.analytics ? 'right-1' : 'left-1'}`}></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Cookie Marketing */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">Cookie di Marketing</h3>
                    <p className="text-slate-600 text-sm mt-1">
                      Utilizzati per tracciare i visitatori sui siti web e mostrare annunci pertinenti. 
                      Attualmente questo sito non utilizza cookie di marketing.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences({...preferences, marketing: !preferences.marketing})}
                      className={`w-12 h-6 rounded-full relative transition-colors ${preferences.marketing ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.marketing ? 'right-1' : 'left-1'}`}></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={rejectAll}
                className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-white font-medium transition-colors"
              >
                Rifiuta Non Necessari
              </button>
              <button
                onClick={saveCustom}
                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors"
              >
                Salva Preferenze
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== PRIVACY POLICY ====================
const PrivacyPolicy = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-6 border-b border-slate-200 flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-slate-900">Privacy Policy</h1>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-2xl">&times;</button>
        </div>

        <div className="p-6 prose prose-slate max-w-none">
          <p className="text-sm text-slate-500 mb-6">Ultimo aggiornamento: Gennaio 2026</p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">1. Titolare del Trattamento</h2>
          <p>
            Il Titolare del trattamento dei dati personali è:<br/>
            <strong>Studio Dott. Alberto Grassetti</strong><br/>
            Via Umberto I° 19, 33097 Spilimbergo (PN)<br/>
            P.IVA: IT01484710932 - C.F.: GRSLRT63S03I904B<br/>
            Email: info@consulenze-aziendali.it<br/>
            PEC: consaz@legalmail.it<br/>
            Tel: +39 0427 51251
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">2. Tipologie di Dati Raccolti</h2>
          <p>Il Titolare raccoglie le seguenti categorie di dati personali:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Dati di contatto:</strong> nome, cognome, email, telefono (forniti tramite form di contatto)</li>
            <li><strong>Dati di navigazione:</strong> indirizzo IP, tipo di browser, pagine visitate, data e ora di accesso</li>
            <li><strong>Dati dell'Area Clienti:</strong> credenziali di accesso, codice fiscale, partita IVA (per clienti registrati)</li>
            <li><strong>Cookie:</strong> come descritto nella Cookie Policy</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">3. Finalità del Trattamento</h2>
          <p>I dati personali sono trattati per le seguenti finalità:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Rispondere alle richieste:</strong> gestire le richieste inviate tramite il form di contatto</li>
            <li><strong>Erogazione servizi:</strong> fornire accesso all'Area Clienti e ai documenti</li>
            <li><strong>Obblighi di legge:</strong> adempiere a obblighi previsti dalla normativa vigente</li>
            <li><strong>Miglioramento del servizio:</strong> analisi statistiche anonime per migliorare il sito</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">4. Base Giuridica del Trattamento</h2>
          <p>Il trattamento dei dati si basa su:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Consenso:</strong> per l'invio di comunicazioni e l'uso di cookie non necessari (Art. 6.1.a GDPR)</li>
            <li><strong>Esecuzione contrattuale:</strong> per l'erogazione dei servizi richiesti (Art. 6.1.b GDPR)</li>
            <li><strong>Obbligo legale:</strong> per adempimenti fiscali e normativi (Art. 6.1.c GDPR)</li>
            <li><strong>Legittimo interesse:</strong> per la sicurezza del sito e prevenzione frodi (Art. 6.1.f GDPR)</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">5. Conservazione dei Dati</h2>
          <p>I dati personali sono conservati per il tempo strettamente necessario:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Dati di contatto:</strong> 24 mesi dalla richiesta, salvo instaurazione di rapporto professionale</li>
            <li><strong>Dati Area Clienti:</strong> per tutta la durata del rapporto e per 10 anni successivi (obblighi fiscali)</li>
            <li><strong>Dati di navigazione:</strong> 12 mesi</li>
            <li><strong>Cookie:</strong> secondo quanto indicato nella Cookie Policy</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">6. Diritti dell'Interessato</h2>
          <p>Ai sensi degli artt. 15-22 del GDPR, l'interessato ha diritto di:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Accesso:</strong> ottenere conferma del trattamento e copia dei dati</li>
            <li><strong>Rettifica:</strong> correggere dati inesatti o incompleti</li>
            <li><strong>Cancellazione:</strong> richiedere la cancellazione dei dati ("diritto all'oblio")</li>
            <li><strong>Limitazione:</strong> limitare il trattamento in determinati casi</li>
            <li><strong>Portabilità:</strong> ricevere i dati in formato strutturato</li>
            <li><strong>Opposizione:</strong> opporsi al trattamento per motivi legittimi</li>
            <li><strong>Revoca consenso:</strong> revocare il consenso in qualsiasi momento</li>
          </ul>
          <p className="mt-4">
            Per esercitare i propri diritti, contattare: <strong>info@consulenze-aziendali.it</strong>
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">7. Reclamo all'Autorità</h2>
          <p>
            L'interessato ha diritto di proporre reclamo al Garante per la Protezione dei Dati Personali:<br/>
            <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
              www.garanteprivacy.it
            </a>
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">8. Sicurezza dei Dati</h2>
          <p>
            Il Titolare adotta misure tecniche e organizzative adeguate per proteggere i dati personali 
            da accessi non autorizzati, perdita, distruzione o divulgazione, inclusi:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Crittografia delle comunicazioni (HTTPS/SSL)</li>
            <li>Accesso limitato ai dati solo al personale autorizzato</li>
            <li>Backup periodici e procedure di disaster recovery</li>
            <li>Password hash per le credenziali di accesso</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">9. Trasferimento Dati</h2>
          <p>
            I dati personali sono trattati presso la sede del Titolare e presso i server del provider 
            di hosting situati nell'Unione Europea. Non vengono trasferiti dati verso paesi terzi.
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">10. Modifiche alla Privacy Policy</h2>
          <p>
            Il Titolare si riserva il diritto di modificare questa Privacy Policy. Le modifiche saranno 
            pubblicate su questa pagina con indicazione della data di ultimo aggiornamento.
          </p>
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors"
          >
            Ho letto e compreso
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== COOKIE POLICY ====================
const CookiePolicy = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-6 border-b border-slate-200 flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-slate-900">Cookie Policy</h1>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-2xl">&times;</button>
        </div>

        <div className="p-6 prose prose-slate max-w-none">
          <p className="text-sm text-slate-500 mb-6">Ultimo aggiornamento: Gennaio 2026</p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">1. Cosa sono i Cookie</h2>
          <p>
            I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell'utente 
            durante la navigazione. Vengono utilizzati per memorizzare informazioni e preferenze, 
            migliorando l'esperienza di navigazione.
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">2. Tipologie di Cookie Utilizzati</h2>
          
          <h3 className="text-lg font-semibold text-slate-900 mt-4">Cookie Tecnici/Necessari</h3>
          <p>Essenziali per il funzionamento del sito. Non richiedono consenso.</p>
          <table className="w-full border-collapse border border-slate-300 mt-2 text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="border border-slate-300 p-2 text-left">Nome</th>
                <th className="border border-slate-300 p-2 text-left">Finalità</th>
                <th className="border border-slate-300 p-2 text-left">Durata</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-slate-300 p-2">cookieConsent</td>
                <td className="border border-slate-300 p-2">Memorizza le preferenze sui cookie</td>
                <td className="border border-slate-300 p-2">12 mesi</td>
              </tr>
              <tr>
                <td className="border border-slate-300 p-2">token</td>
                <td className="border border-slate-300 p-2">Autenticazione Area Clienti</td>
                <td className="border border-slate-300 p-2">24 ore</td>
              </tr>
            </tbody>
          </table>

          <h3 className="text-lg font-semibold text-slate-900 mt-4">Cookie Analitici</h3>
          <p>Utilizzati per raccogliere informazioni statistiche anonime sull'utilizzo del sito.</p>
          <p className="text-slate-600 italic">
            Attualmente questo sito non utilizza cookie analitici di terze parti.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-4">Cookie di Marketing</h3>
          <p>Utilizzati per tracciare i visitatori e mostrare annunci pertinenti.</p>
          <p className="text-slate-600 italic">
            Attualmente questo sito non utilizza cookie di marketing.
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">3. Gestione dei Cookie</h2>
          <p>
            Puoi gestire le preferenze sui cookie in qualsiasi momento cliccando su 
            "Impostazioni Cookie" nel footer del sito, oppure attraverso le impostazioni del tuo browser.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-4">Come disabilitare i cookie dal browser:</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Chrome:</strong> Impostazioni → Privacy e sicurezza → Cookie</li>
            <li><strong>Firefox:</strong> Impostazioni → Privacy e sicurezza → Cookie</li>
            <li><strong>Safari:</strong> Preferenze → Privacy → Gestisci dati siti web</li>
            <li><strong>Edge:</strong> Impostazioni → Cookie e autorizzazioni sito</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">4. Conseguenze della Disabilitazione</h2>
          <p>
            La disabilitazione dei cookie tecnici potrebbe compromettere alcune funzionalità del sito, 
            come l'accesso all'Area Clienti o il salvataggio delle preferenze.
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">5. Contatti</h2>
          <p>
            Per informazioni sulla gestione dei cookie:<br/>
            <strong>Studio Dott. Alberto Grassetti</strong><br/>
            Email: info@consulenze-aziendali.it<br/>
            Tel: +39 0427 51251
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-6">6. Aggiornamenti</h2>
          <p>
            Questa Cookie Policy può essere aggiornata periodicamente. Ti invitiamo a consultare 
            regolarmente questa pagina per essere informato su eventuali modifiche.
          </p>
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors"
          >
            Ho letto e compreso
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN WEBSITE ====================
const MainWebsite = ({ onLoginClick }) => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookie, setShowCookie] = useState(false);

  return (
    <>
      <Navbar onLoginClick={onLoginClick} />
      <HeroSection />
      <ServicesSection />
      <AboutSection />
      <RisorseUtiliSection />
      <ScadenzeFiscaliSection />
      <FAQSection />
      <CalcolatoreTasseSection />
      <ContactSection onOpenPrivacy={() => setShowPrivacy(true)} />
      <Footer onOpenPrivacy={() => setShowPrivacy(true)} onOpenCookie={() => setShowCookie(true)} />
      <AIChatbot />
      <CookieBanner onOpenPrivacy={() => setShowPrivacy(true)} onOpenCookie={() => setShowCookie(true)} />
      
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showCookie && <CookiePolicy onClose={() => setShowCookie(false)} />}
    </>
  );
};

// ==================== SET PASSWORD PAGE ====================
const SetPasswordPage = ({ token, onComplete }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Verifica validità token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/auth/verifica-token/${token}`);
        if (response.data.valido) {
          setTokenInfo(response.data);
        } else {
          setError("Il link non è più valido o è scaduto. Contatta lo studio per richiedere un nuovo link.");
        }
      } catch (e) {
        setError("Errore nella verifica del link. Riprova più tardi.");
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      setError("La password deve essere di almeno 8 caratteri");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Le password non corrispondono");
      return;
    }
    
    setSubmitting(true);
    setError("");
    
    try {
      await axios.post(`${API}/auth/imposta-password?token=${encodeURIComponent(token)}&nuova_password=${encodeURIComponent(password)}`);
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore nell'impostazione della password");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700">
        <div className="text-center text-white">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Verifica link in corso...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Impostata!</h1>
          <p className="text-gray-600 mb-6">
            La tua password è stata impostata con successo. Ora puoi accedere all'Area Clienti.
          </p>
          <button
            onClick={onComplete}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            Vai all'Accesso
          </button>
        </div>
      </div>
    );
  }

  if (error && !tokenInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Non Valido</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={onComplete}
            className="w-full py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
          >
            Torna al Sito
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Imposta la tua Password</h1>
          <p className="text-gray-500 mt-1">Studio Dott. Alberto Grassetti</p>
        </div>

        {tokenInfo && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              Benvenuto <strong>{tokenInfo.nome} {tokenInfo.cognome}</strong>!<br/>
              Imposta la password per il tuo account: <strong>{tokenInfo.email}</strong>
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nuova Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Minimo 8 caratteri"
              required
              minLength={8}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Conferma Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ripeti la password"
              required
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Salvataggio...
              </>
            ) : "Imposta Password"}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          Hai bisogno di assistenza? Contatta lo studio al +39 0427 51251
        </p>
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  // Check for reset_token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <AuthProvider>
      <AppContent 
        showLogin={showLogin} 
        setShowLogin={setShowLogin} 
        resetToken={resetToken}
        setResetToken={setResetToken}
      />
    </AuthProvider>
  );
}

const AppContent = ({ showLogin, setShowLogin, resetToken, setResetToken }) => {
  const { user, loading } = useAuth();

  // Se c'è un token di reset, mostra il form per impostare password
  if (resetToken) {
    return <SetPasswordPage token={resetToken} onComplete={() => setResetToken(null)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Se l'utente è loggato, mostra la dashboard appropriata
  if (user) {
    if (user.user_type === "admin") {
      return <AdminDashboard />;
    } else {
      return <ClienteDashboard />;
    }
  }

  // Se siamo nella pagina login
  if (showLogin) {
    return <LoginPage onBack={() => setShowLogin(false)} />;
  }

  // Altrimenti mostra il sito principale
  return <MainWebsite onLoginClick={() => setShowLogin(true)} />;
};

export default App;
